import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { employeeApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { useUnsavedGuard } from '../../hooks/useUnsavedWork.jsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import { ArrowLeft, Upload, Plus, Send, X, Plane, ArrowRight, ArrowLeftRight, Search, Loader2, Users } from 'lucide-react';
import { cn } from '@/lib/utils';

// A "preference" is one option the employee offers to the travel manager.
// Each preference has one or more outbound segments. For round-trip
// requests, the same preference also carries one or more return segments.
// Multi-segment lists describe the case where a leg is split across
// separate bookings.
function emptySegment() {
  return { airline: '', number: '', from: '', to: '', date: '', time: '' };
}
function emptyPref() {
  return { segments: [emptySegment()], returnSegments: [emptySegment()] };
}

export default function NewTravelRequestPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [passport, setPassport] = useState(null);
  const [tripType, setTripType] = useState('one_way'); // 'one_way' | 'round_trip'
  const [form, setForm] = useState({
    departureLocation: '',
    finalDestination: '',
    departureDate: '',
    arrivalDate: '',
    reason: '',
    travelPurpose: '',
  });
  const [prefs, setPrefs] = useState([emptyPref(), emptyPref(), emptyPref()]);

  // Group travel: colleagues you're travelling with. Each selected person gets
  // their own trip record (sharing a groupId) so it shows in THEIR My Travel,
  // while the travel team sees one request.
  const [colleagues, setColleagues] = useState([]);
  const [travellerIds, setTravellerIds] = useState([]);
  const [travellerSearch, setTravellerSearch] = useState('');

  // Substring match on name OR department, so typing "finance" pulls up
  // everyone in that department as well as anyone named it.
  const q = travellerSearch.trim().toLowerCase();
  const visibleColleagues = q
    ? colleagues.filter((c) =>
        (c.displayName || '').toLowerCase().includes(q) ||
        (c.department || '').toLowerCase().includes(q))
    : colleagues;

  // Kept separate from the filtered list so chips survive a search.
  const selectedColleagues = colleagues.filter((c) => travellerIds.includes(c.id));

  useEffect(() => {
    employeeApi.listColleagues()
      .then((res) => setColleagues(res.users || []))
      .catch(() => setColleagues([]));
  }, []);

  // No server draft for this form — the unsaved-work guard (+ the native
  // beforeunload prompt it wires up) is the only loss-prevention. Dirty is
  // a simple comparison against the empty initial state.
  const initialSnapshotRef = useRef(JSON.stringify({ form, tripType, prefs }));
  const isDirty = JSON.stringify({ form, tripType, prefs }) !== initialSnapshotRef.current || !!passport;
  useUnsavedGuard({
    id: 'travel-request-new',
    label: `Travel request: ${form.finalDestination || 'new'}`,
    href: '/travel/new',
    dirty: isDirty,
  });

  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  // `which` is 'segments' (outbound) or 'returnSegments' (round-trip return).
  const setSegment = (prefIdx, which, segIdx, field, value) => {
    setPrefs((prev) => prev.map((pref, i) => {
      if (i !== prefIdx) return pref;
      const list = pref[which].map((s, si) => (si === segIdx ? { ...s, [field]: value } : s));
      return { ...pref, [which]: list };
    }));
  };
  const addSegment = (prefIdx, which) => {
    setPrefs((prev) => prev.map((pref, i) => (i === prefIdx
      ? { ...pref, [which]: [...pref[which], emptySegment()] }
      : pref)));
  };
  const removeSegment = (prefIdx, which, segIdx) => {
    setPrefs((prev) => prev.map((pref, i) => {
      if (i !== prefIdx) return pref;
      return { ...pref, [which]: pref[which].filter((_, si) => si !== segIdx) };
    }));
  };

  // Auto-fill a segment from its flight number via the server-side lookup.
  // Best-effort: fills airline/route/time, keeps the date the user entered,
  // and just toasts when the lookup isn't configured or finds nothing.
  const lookupSegment = async (prefIdx, which, segIdx) => {
    const seg = prefs[prefIdx][which][segIdx];
    const number = (seg.number || '').trim();
    if (!number) { toast.error('Enter a flight number first.'); return; }
    try {
      const res = await employeeApi.lookupFlight(number);
      if (res?.configured === false) {
        toast.info('Flight lookup is not set up yet — fill the details in manually.');
        return;
      }
      if (!res?.found) {
        toast.warning('Could not find that flight — check the number, or fill the details in manually.');
        return;
      }
      setPrefs((prev) => prev.map((pref, i) => {
        if (i !== prefIdx) return pref;
        const list = pref[which].map((s, si) => (si === segIdx ? {
          ...s,
          airline: res.airline || s.airline,
          from: res.from || s.from,
          to: res.to || s.to,
          time: res.time || s.time,
        } : s));
        return { ...pref, [which]: list };
      }));
      toast.success('Flight details filled in — double-check the date.');
    } catch (err) {
      toast.error(`Lookup failed: ${err.message}`);
    }
  };

  const handleSubmit = async () => {
    if (!form.departureLocation || !form.finalDestination || !form.departureDate || !form.arrivalDate) {
      const dateLabel = tripType === 'round_trip' ? 'departure and return dates' : 'departure and arrival dates';
      toast.error(`Departure, destination, and ${dateLabel} are required`);
      return;
    }
    if (tripType === 'round_trip' && form.arrivalDate < form.departureDate) {
      toast.error('Return date must be on or after departure date');
      return;
    }
    if (!form.travelPurpose) {
      toast.error('Please select a travel purpose');
      return;
    }

    const cleanSegments = (list) => list
      .map((s) => ({
        airline: s.airline.trim(),
        number: s.number.trim(),
        from: s.from.trim(),
        to: s.to.trim(),
        date: s.date,
        time: s.time || '',
      }))
      .filter((s) => s.airline || s.number || s.from || s.to || s.date);

    // Strip empty segments per leg, then drop preferences that ended up
    // empty across both legs.
    const cleanedPrefs = prefs
      .map((pref) => {
        const segments = cleanSegments(pref.segments);
        const returnSegments = tripType === 'round_trip' ? cleanSegments(pref.returnSegments) : [];
        return { segments, returnSegments };
      })
      .filter((pref) => pref.segments.length > 0 || pref.returnSegments.length > 0);

    setSubmitting(true);
    try {
      const fd = new FormData();
      if (passport) fd.append('passport', passport);
      fd.append('tripType', tripType);
      fd.append('departureLocation', form.departureLocation);
      fd.append('finalDestination', form.finalDestination);
      fd.append('departureDate', form.departureDate);
      fd.append('arrivalDate', form.arrivalDate);
      if (form.reason) fd.append('reason', form.reason);
      fd.append('travelPurpose', form.travelPurpose);
      fd.append('preferences', JSON.stringify(cleanedPrefs));
      fd.append('travellerIds', JSON.stringify(travellerIds));

      await employeeApi.submitTravel(fd);
      toast.success('Travel request submitted! The office manager will be notified.');
      navigate('/travel');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = form.departureLocation && form.finalDestination && form.departureDate && form.arrivalDate && form.travelPurpose;
  const isRoundTrip = tripType === 'round_trip';

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={() => navigate('/travel')} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors cursor-pointer">
        <ArrowLeft className="h-4 w-4" /> Back to Travel
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">New Travel Request</h1>
        <p className="text-muted-foreground">Submit your travel details for booking</p>
      </div>

      <div className="space-y-6">
        <Card>
          <CardContent className="py-4">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setTripType('one_way')}
                className={cn(
                  'flex items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-semibold transition-all cursor-pointer',
                  tripType === 'one_way'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-input text-muted-foreground hover:bg-muted'
                )}
              >
                <ArrowRight className="h-4 w-4" /> One-way
              </button>
              <button
                type="button"
                onClick={() => setTripType('round_trip')}
                className={cn(
                  'flex items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-semibold transition-all cursor-pointer',
                  tripType === 'round_trip'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-input text-muted-foreground hover:bg-muted'
                )}
              >
                <ArrowLeftRight className="h-4 w-4" /> Round-trip
              </button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Travel Details</CardTitle>
            <CardDescription>Where and when you need to travel</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input label="Departure Location *" value={form.departureLocation} onChange={(e) => set('departureLocation', e.target.value)} placeholder="e.g. Riyadh, KSA" />
              <Input
                label={isRoundTrip ? 'Destination *' : 'Final Destination *'}
                value={form.finalDestination}
                onChange={(e) => set('finalDestination', e.target.value)}
                placeholder="e.g. London, UK"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input label="Departure Date *" type="date" value={form.departureDate} onChange={(e) => set('departureDate', e.target.value)} />
              <Input
                label={isRoundTrip ? 'Return Date *' : 'Arrival Date *'}
                type="date"
                value={form.arrivalDate}
                onChange={(e) => set('arrivalDate', e.target.value)}
              />
            </div>
            <Input label="Reason for Travel" value={form.reason} onChange={(e) => set('reason', e.target.value)} placeholder="e.g. Client meeting, conference attendance" />
            <div>
              <label className="block text-[13px] font-medium text-foreground mb-2">Purpose *</label>
              <div className="flex gap-4">
                {[{ value: 'project', label: 'Project' }, { value: 'annual_flight', label: 'Annual Flight' }, { value: 'other', label: 'Other' }].map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="travelPurpose"
                      value={opt.value}
                      checked={form.travelPurpose === opt.value}
                      onChange={(e) => set('travelPurpose', e.target.value)}
                      className="accent-primary"
                    />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Flight Preferences (Optional)</CardTitle>
            <CardDescription>
              List up to 3 preferred options. If an option requires separate
              bookings for each connection, add a flight for each booking.
              {isRoundTrip && ' Round-trip preferences include both outbound and return flights.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {prefs.map((pref, prefIdx) => (
              <div key={prefIdx} className="rounded-lg border border-border p-4 space-y-3">
                <div className="text-sm font-semibold">Preference {prefIdx + 1}</div>

                <SegmentList
                  heading={isRoundTrip ? 'Outbound' : null}
                  segments={pref.segments}
                  onChange={(segIdx, field, value) => setSegment(prefIdx, 'segments', segIdx, field, value)}
                  onAdd={() => addSegment(prefIdx, 'segments')}
                  onRemove={(segIdx) => removeSegment(prefIdx, 'segments', segIdx)}
                  onLookup={(segIdx) => lookupSegment(prefIdx, 'segments', segIdx)}
                />

                {isRoundTrip && (
                  <SegmentList
                    heading="Return"
                    segments={pref.returnSegments}
                    onChange={(segIdx, field, value) => setSegment(prefIdx, 'returnSegments', segIdx, field, value)}
                    onAdd={() => addSegment(prefIdx, 'returnSegments')}
                    onRemove={(segIdx) => removeSegment(prefIdx, 'returnSegments', segIdx)}
                    onLookup={(segIdx) => lookupSegment(prefIdx, 'returnSegments', segIdx)}
                  />
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" /> Who is travelling?
            </CardTitle>
            <CardDescription>
              You are always included. Add colleagues to book one trip for everyone — each
              person gets their own trip to track, and the travel team sees a single request.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative mb-2">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={travellerSearch}
                onChange={(e) => setTravellerSearch(e.target.value)}
                placeholder="Search by name or department…"
                className="w-full rounded-lg border border-border bg-card py-2 pl-9 pr-8 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              {travellerSearch && (
                <button
                  type="button"
                  onClick={() => setTravellerSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="max-h-56 overflow-y-auto rounded-lg border border-border p-1">
              {colleagues.length === 0 ? (
                <div className="p-3 text-sm text-muted-foreground">Loading colleagues…</div>
              ) : visibleColleagues.length === 0 ? (
                <div className="p-3 text-sm text-muted-foreground">
                  No colleague matches &ldquo;{travellerSearch}&rdquo;.
                </div>
              ) : visibleColleagues.map((c) => (
                <label
                  key={c.id}
                  className="flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 hover:bg-muted"
                >
                  <input
                    type="checkbox"
                    checked={travellerIds.includes(c.id)}
                    onChange={(e) => setTravellerIds((prev) =>
                      e.target.checked ? [...prev, c.id] : prev.filter((x) => x !== c.id)
                    )}
                    className="h-4 w-4 accent-primary"
                  />
                  <span className="text-sm font-medium">{c.displayName}</span>
                  {c.department && (
                    <span className="text-xs text-muted-foreground">· {c.department}</span>
                  )}
                </label>
              ))}
            </div>

            {/* Selections are listed outside the scrolling list so a search
                that filters them out of view can't make you think you lost
                them. Each chip removes that person. */}
            {selectedColleagues.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {selectedColleagues.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setTravellerIds((prev) => prev.filter((x) => x !== c.id))}
                    className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary hover:bg-primary/20"
                  >
                    {c.displayName}
                    <X className="h-3 w-3" />
                  </button>
                ))}
              </div>
            )}

            {travellerIds.length > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                {travellerIds.length + 1} travellers on this trip. Each is emailed and asked to
                upload their own passport.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Passport</CardTitle>
            <CardDescription>
              {travellerIds.length > 0
                ? 'Upload your own passport scan. Your colleagues add theirs from My Travel.'
                : 'Upload a scan of your passport'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <label className="flex items-center gap-3 rounded-lg border border-dashed border-border px-4 py-4 cursor-pointer hover:border-primary/30 hover:bg-muted/30 transition-all">
              <Upload className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium text-foreground">{passport ? passport.name : 'Choose file...'}</p>
                <p className="text-xs text-muted-foreground">PDF, JPG, or PNG</p>
              </div>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => setPassport(e.target.files?.[0] || null)} />
            </label>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSubmit} loading={submitting} disabled={submitting || !canSubmit}>
            <Send className="h-4 w-4" /> Submit Travel Request
          </Button>
        </div>
      </div>
    </div>
  );
}

function SegmentList({ heading, segments, onChange, onAdd, onRemove, onLookup }) {
  const [lookupIdx, setLookupIdx] = useState(-1);
  const doLookup = async (segIdx) => {
    setLookupIdx(segIdx);
    try { await onLookup(segIdx); } finally { setLookupIdx(-1); }
  };
  return (
    <div className="space-y-2">
      {heading && (
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {heading === 'Outbound' ? <ArrowRight className="h-3.5 w-3.5" /> : <ArrowLeft className="h-3.5 w-3.5" />}
          {heading}
        </div>
      )}
      {segments.map((seg, segIdx) => (
        <div key={segIdx} className="rounded-md border border-border/60 bg-muted/20 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <Plane className="h-3.5 w-3.5" />
              {segments.length === 1
                ? 'Flight'
                : segIdx === 0 ? 'First booking'
                : segIdx === segments.length - 1 ? 'Final booking'
                : `Booking ${segIdx + 1}`}
            </div>
            {segments.length > 1 && (
              <button
                type="button"
                onClick={() => onRemove(segIdx)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive transition-colors cursor-pointer"
                aria-label={`Remove booking ${segIdx + 1}`}
              >
                <X className="h-3.5 w-3.5" /> Remove
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input value={seg.airline} onChange={(e) => onChange(segIdx, 'airline', e.target.value)} placeholder="Airline (e.g. Emirates)" />
            <Input value={seg.number} onChange={(e) => onChange(segIdx, 'number', e.target.value)} placeholder="Flight # (e.g. EK803)" />
          </div>
          <button
            type="button"
            onClick={() => doLookup(segIdx)}
            disabled={lookupIdx === segIdx || !seg.number.trim()}
            className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border/70 py-1.5 text-xs font-medium text-muted-foreground hover:border-primary/40 hover:text-foreground hover:bg-muted/30 transition-all disabled:opacity-40 disabled:pointer-events-none cursor-pointer"
          >
            {lookupIdx === segIdx ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
            Look up flight number to fill airline, route and time
          </button>
          <div className="grid grid-cols-2 gap-2">
            <Input value={seg.from} onChange={(e) => onChange(segIdx, 'from', e.target.value)} placeholder="From (e.g. RUH)" />
            <Input value={seg.to} onChange={(e) => onChange(segIdx, 'to', e.target.value)} placeholder="To (e.g. DXB)" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input label="Date" type="date" value={seg.date} onChange={(e) => onChange(segIdx, 'date', e.target.value)} />
            <Input label="Preferred time" type="time" value={seg.time || ''} onChange={(e) => onChange(segIdx, 'time', e.target.value)} />
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={onAdd}
        className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-border py-2 text-xs font-medium text-muted-foreground hover:border-primary/40 hover:text-foreground hover:bg-muted/30 transition-all cursor-pointer"
      >
        <Plus className="h-3.5 w-3.5" /> Add connected flight (separate booking)
      </button>
    </div>
  );
}
